import { createSelector } from '@reduxjs/toolkit';
import { get, getOr, find, map, pipe, propertyOf, isNil } from 'lodash/fp';
import { ShareType } from 'app/lib/types/server';
import { getCurrentCombinedSlug } from 'app/routes';
import {
    COMBINE_TYPE,
    QUERY_BUILDER_TYPE,
    JOIN_TYPE,
    GENERIC_TRANSFORM_TYPE,
    SQL_VIEW_DTO_TYPE
} from 'app/store/__generated__/types';
import { resolveIds } from 'app/store/utils';
import {
    selectProject,
    selectTransformationsInCurrentProject,
    selectErrors,
    selectWarnings
} from 'app/store/projects/selectors.js';
import { selectEntitiesById, selectEntityIdsBySlug } from 'app/store/entities/selectors.js';
import { selectUsersByUserId } from 'app/store/users/selectors.js';
import { selectSelectedDatasetHasError } from 'app/store/datasetData/selectors.js';
import { selectHasAccessibleDataSourcesForPropEntityId } from 'app/store/access/selectors';
import { selectEmailDialogAssetId } from 'app/store/shares/selectors.js';
import { Defaults } from 'app/lib/constants';
import { decorateViewEntity } from './utils.js';

export const selectCurrentViewId = createSelector(
    getCurrentCombinedSlug,
    selectEntityIdsBySlug,
    (currentSlug, entityIdsBySlug) => getOr(null, currentSlug, entityIdsBySlug)
);

export const selectView = createSelector(
    selectCurrentViewId,
    selectEntitiesById,
    selectUsersByUserId,
    selectErrors,
    selectWarnings,
    (viewId, entitiesById, usersById, errors, warnings) => {
        const entity = entitiesById?.[viewId] || null;
        if (!entity) {
            return null;
        }
        return decorateViewEntity(entity, usersById, entitiesById, errors, warnings);
    }
);

const selectPropEntityId = (_, props) => props.entityId;
const selectViewForPropEntityId = createSelector(
    selectEntitiesById,
    selectUsersByUserId,
    selectErrors,
    selectPropEntityId,
    (entitiesById, usersById, errors, entityId) => {
        const entity = entitiesById?.[entityId] || null;
        if (!entity) {
            return null;
        }
        return decorateViewEntity(entity, usersById, entitiesById, errors);
    }
);

const selectViewUid = createSelector([selectView], get('uid'));
export const selectViewId = createSelector([selectView], get('id'));
export const selectViewErrors = createSelector([selectView], (view) =>
    get(['error', 'errors'], view)
);

export const selectOperationsForCurrentView = createSelector([selectView], (view) =>
    view ? resolveIds(view.transformationsByUid, view.operationUids) : []
);

export const selectOperationsForPropEntityId = createSelector(
    [selectViewForPropEntityId],
    (view) => (view ? resolveIds(view.transformationsByUid, view.operationUids) : [])
);

export const selectSourceSchemaForCurrentView = createSelector(
    selectViewUid,
    selectTransformationsInCurrentProject,
    selectProject,
    (viewId, viewsInCurrentSpace, space) => {
        const sourceViewUid = pipe(
            find((d) => d.fromView === viewId),
            get('toView')
        )(space?.dependencies);
        const sourceView = viewsInCurrentSpace.find(({ uid }) => uid === sourceViewUid);

        return get(['schemasByUid', sourceView ? sourceViewUid : viewId, 'columns'], space);
    }
);

export const selectSchemaForCurrentView = createSelector(
    selectViewUid,
    selectProject,
    (viewId, project) => get(['schemasByUid', viewId, 'columns'], project)
);

export const selectSchemasForCurrentView = createSelector(
    selectView,
    selectSchemaForCurrentView,
    (view, schema) => {
        if (
            view?._type === SQL_VIEW_DTO_TYPE ||
            view?._type === COMBINE_TYPE ||
            view?._type === QUERY_BUILDER_TYPE ||
            view?._type === JOIN_TYPE ||
            view?._type === GENERIC_TRANSFORM_TYPE
        ) {
            return [schema];
        }

        const schemasByUid = getOr(Defaults.OBJECT, 'schemasByUid', view);
        const operationUids = getOr(Defaults.ARRAY, 'operationUids', view);
        const toColumnArrays = pipe(map(propertyOf(schemasByUid)), map('columns'));

        return toColumnArrays(operationUids);
    }
);

export const selectCanAddTransformToView = createSelector(
    selectView,
    selectHasAccessibleDataSourcesForPropEntityId,
    selectSelectedDatasetHasError,
    (view, hasAccessibleDataSources, selectedDatasetHasError) => {
        const hasAccessibleSources = !isNil(hasAccessibleDataSources) && hasAccessibleDataSources;
        const hasNoErrors = isNil(view?.error) && !selectedDatasetHasError;
        const isNoDeployment = isNil(view?.deployedFrom);
        const isNoShareNode =
            !isNil(view?._type) &&
            view._type !== ShareType.GOOGLE_SHEETS &&
            view._type !== ShareType.EMAIL;

        return hasAccessibleSources && hasNoErrors && isNoDeployment && isNoShareNode;
    }
);

const selectSourceView = createSelector([selectView, selectEntitiesById], (view, entitiesById) => {
    if (!view) {
        return null;
    }
    const entityId = `${view.spaceUid}.${view.sourceView}`;

    return entitiesById[entityId];
});

export const selectSourceViewName = createSelector(selectSourceView, (view) => view?.name);

export const selectViewNameForEmailDialog = createSelector(
    selectEmailDialogAssetId,
    selectCurrentViewId,
    selectEntitiesById,
    (assetId, currentViewId, entitiesById) => {
        const entity = entitiesById[assetId || currentViewId];
        return entity?.name ?? null;
    }
);
